// utils/compressToWebp.js

/**
 * Сжимает изображение в WebP с ресайзом по ширине
 * и автоподбором качества под целевой размер файла.
 *
 * @param {File} file
 * @param {Object} [options]
 * @param {number} [options.maxWidth=1280]     максимальная ширина результата
 * @param {number} [options.targetSizeKB=250]  к чему стремимся по весу
 * @param {number} [options.minQuality=0.4]    не опускаемся ниже этого качества
 * @param {number} [options.maxQuality=0.9]    не поднимаемся выше (нет смысла)
 * @param {string} [options.mimeType]          формат вывода (по умолчанию image/webp
 *                                             c фолбэком на image/jpeg)
 * @returns {Promise<File>}
 */
export function compressToWebp(file, {
  maxWidth = 1280,
  targetSizeKB = 250,
  minQuality = 0.4,
  maxQuality = 0.9,
  mimeType = null,
} = {}) {
  return new Promise((resolve, reject) => {
    const outputType = mimeType || (isWebpSupported() ? 'image/webp' : 'image/jpeg')
    const outputExt = outputType === 'image/webp' ? '.webp' : '.jpg'

    const img = new Image()
    const reader = new FileReader()

    reader.onload = (e) => { img.src = e.target.result }
    reader.onerror = reject

    img.onload = async () => {
      let { width, height } = img

      // Апскейл не нужен — только ресайз вниз с сохранением пропорций
      if (width > maxWidth) {
        const ratio = maxWidth / width
        width = maxWidth
        height = Math.round(height * ratio)
      }

      const canvas = document.createElement('canvas')
      canvas.width = width
      canvas.height = height
      const ctx = canvas.getContext('2d')
      ctx.drawImage(img, 0, 0, width, height)

      try {
        const blob = await findOptimalQuality(canvas, outputType, targetSizeKB, minQuality, maxQuality)
        if (!blob) {
          reject(new Error('Canvas toBlob failed'))
          return
        }
        const compressedFile = new File(
          [blob],
          file.name.replace(/\.[^.]+$/, outputExt),
          { type: outputType, lastModified: Date.now() }
        )
        resolve(compressedFile)
      } catch (err) {
        reject(err)
      }
    }

    img.onerror = reject
    reader.readAsDataURL(file)
  })
}

// Бинарный поиск quality, чтобы попасть в целевой размер файла
async function findOptimalQuality(canvas, mimeType, targetSizeKB, minQ, maxQ) {
  const targetBytes = targetSizeKB * 1024
  const toBlob = (q) => new Promise((res) => canvas.toBlob(res, mimeType, q))

  let low = minQ
  let high = maxQ
  let bestBlob = await toBlob(high)

  // если даже макс. качество уже уложилось в лимит — не паримся
  if (bestBlob && bestBlob.size <= targetBytes) {
    return bestBlob
  }

  // 6 итераций бинарного поиска обычно достаточно
  for (let i = 0; i < 6; i++) {
    const mid = (low + high) / 2
    const blob = await toBlob(mid)
    if (!blob) break

    if (blob.size > targetBytes) {
      high = mid
    } else {
      bestBlob = blob
      low = mid
    }
  }

  return bestBlob
}

let _webpSupport = null
export function isWebpSupported() {
  if (_webpSupport !== null) return _webpSupport
  try {
    const canvas = document.createElement('canvas')
    _webpSupport = canvas.toDataURL('image/webp').startsWith('data:image/webp')
  } catch (e) {
    _webpSupport = false
  }
  return _webpSupport
}

// Форматы, которые НЕ пережимаем через canvas:
// gif — потеряет анимацию, svg — вектор растеризовать нельзя.
const SKIP_COMPRESSION_TYPES = new Set(['image/gif', 'image/svg+xml'])

/**
 * Пережимает файл, только если это подходящее растровое изображение.
 * Для видео, gif, svg и любых нештатных ошибок возвращает исходный файл
 * без изменений — чтобы загрузка никогда не ломалась из-за сжатия.
 *
 * @param {File} file
 * @param {Object} [options] — те же опции, что и у compressToWebp
 * @returns {Promise<File>}
 */
export async function maybeCompressImage(file, options = {}) {
  if (!file || !file.type || !file.type.startsWith('image/')) return file
  if (SKIP_COMPRESSION_TYPES.has(file.type)) return file

  try {
    const compressed = await compressToWebp(file, options)
    // Если сжатие внезапно раздуло файл — оставляем оригинал
    return compressed.size < file.size ? compressed : file
  } catch (err) {
    console.warn('Не удалось сжать изображение, загружаем оригинал:', err)
    return file
  }
}
